/**
 * Audit Log Viewer Page
 *
 * Super Admin page for viewing comprehensive audit logs and activity tracking.
 * Shows IP addresses, locations, devices, and all user actions.
 */

import React, { useState, useEffect } from 'react';
import {
  Shield,
  Activity,
  MapPin,
  Monitor,
  Clock,
  User,
  Search,
  Filter,
  Download,
  Eye,
  AlertCircle,
  TrendingUp,
  Globe,
} from 'lucide-react';
import { LoadingState } from '../components/shared/LoadingState';
import { Card } from '../components/shared/Card';

interface AuditLogEntry {
  id: number;
  userId: number;
  userEmail: string;
  userRole: string;
  action: string;
  entityType?: string;
  entityId?: number;
  details: Record<string, unknown>;
  ipAddress: string;
  ipCountry?: string;
  ipRegion?: string;
  ipCity?: string;
  deviceType?: string;
  browser?: string;
  os?: string;
  userAgent?: string;
  createdAt: string;
}

interface ActivityLogEntry {
  id: number;
  userId?: number;
  userEmail?: string;
  userRole?: string;
  activityType: string;
  entityType?: string;
  entityId?: string;
  entityName?: string;
  ipAddress: string;
  ipCountry?: string;
  ipRegion?: string;
  ipCity?: string;
  deviceType?: string;
  browser?: string;
  os?: string;
  responseTimeMs?: number;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export const AuditLogViewer: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'audit' | 'activity'>('activity');
  const [loading, setLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('7d');
  const [activityTypeFilter, setActivityTypeFilter] = useState<string>('');

  useEffect(() => {
    fetchLogs();
  }, [activeTab, dateRange, activityTypeFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      if (activeTab === 'activity') {
        const response = await fetch('/api/admin/audit/activity-log?limit=100', { headers });
        if (!response.ok) throw new Error('Failed to fetch activity logs');
        const data = await response.json();
        setActivityLogs(data.data || data.activities || []);
      } else {
        const response = await fetch('/api/admin/audit-log?limit=100', { headers });
        if (!response.ok) throw new Error('Failed to fetch audit logs');
        const data = await response.json();
        setAuditLogs(data.data || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load logs');
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredActivityLogs = activityLogs.filter(log => {
    if (searchQuery && !log.entityId?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !log.userEmail?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (activityTypeFilter && log.activityType !== activityTypeFilter) {
      return false;
    }
    return true;
  });

  const filteredAuditLogs = auditLogs.filter(log => {
    if (searchQuery && !log.action.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !log.userEmail?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'carrier_view': return <Eye className="w-4 h-4" />;
      case 'vin_search': return <Search className="w-4 h-4" />;
      case 'carrier_search': return <Search className="w-4 h-4" />;
      case 'report_download': return <Download className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getDeviceBadgeColor = (type?: string) => {
    switch (type) {
      case 'mobile': return 'bg-blue-100 text-blue-700';
      case 'tablet': return 'bg-purple-100 text-purple-700';
      case 'desktop': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-500';
    }
  };

  const formatLocation = (log: ActivityLogEntry | AuditLogEntry) => {
    const parts = [];
    if (log.ipCity) parts.push(log.ipCity);
    if (log.ipRegion) parts.push(log.ipRegion);
    if (log.ipCountry) parts.push(log.ipCountry);
    return parts.length > 0 ? parts.join(', ') : log.ipAddress || 'Unknown';
  };

  // Get unique activity types for filter
  const activityTypes = Array.from(new Set(activityLogs.map(log => log.activityType)));

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Audit & Activity Logs</h1>
          </div>
          <p className="text-gray-600">
            Comprehensive tracking of all user activities, admin actions, and system events
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Activities</p>
                <p className="text-2xl font-bold text-gray-900">{activityLogs.length}</p>
              </div>
              <Activity className="w-8 h-8 text-blue-500" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Unique Users</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(activityLogs.map(l => l.userId).filter(Boolean)).size}
                </p>
              </div>
              <User className="w-8 h-8 text-green-500" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Locations</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(activityLogs.map(l => l.ipCountry).filter(Boolean)).size || 1}
                </p>
              </div>
              <Globe className="w-8 h-8 text-purple-500" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Devices</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(activityLogs.map(l => l.deviceType).filter(Boolean)).size}
                </p>
              </div>
              <Monitor className="w-8 h-8 text-orange-500" />
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-4">
            <button
              onClick={() => setActiveTab('activity')}
              className={`px-4 py-2 border-b-2 font-medium transition-colors ${
                activeTab === 'activity'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4" />
                <span>User Activities</span>
                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs">
                  {activityLogs.length}
                </span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('audit')}
              className={`px-4 py-2 border-b-2 font-medium transition-colors ${
                activeTab === 'audit'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <span>Admin Actions</span>
                <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs">
                  {auditLogs.length}
                </span>
              </div>
            </button>
          </nav>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by email, DOT, VIN..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {activeTab === 'activity' && (
              <div className="w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activity Type
                </label>
                <select
                  value={activityTypeFilter}
                  onChange={(e) => setActivityTypeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Types</option>
                  {activityTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="w-32">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Period
              </label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="1d">Today</option>
                <option value="7d">7 days</option>
                <option value="30d">30 days</option>
                <option value="90d">90 days</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Content */}
        {loading ? (
          <LoadingState message="Loading audit logs..." />
        ) : error ? (
          <Card>
            <div className="flex items-center gap-3 text-red-600">
              <AlertCircle className="w-5 h-5" />
              <p>{error}</p>
            </div>
          </Card>
        ) : (
          <>
            {/* Activity Log View */}
            {activeTab === 'activity' && (
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activity</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Response</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredActivityLogs.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                            No activities found
                          </td>
                        </tr>
                      ) : (
                        filteredActivityLogs.map((log) => (
                          <tr key={log.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {new Date(log.createdAt).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="font-medium text-gray-900">{log.userEmail || 'Anonymous'}</div>
                              {log.userRole && (
                                <div className="text-xs text-gray-500">{log.userRole}</div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {getActivityIcon(log.activityType)}
                                <span className="text-sm font-medium text-gray-900">
                                  {(log.activityType || 'unknown').replace(/_/g, ' ')}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {log.entityType && (
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {log.entityType}: {log.entityId}
                                  </div>
                                  {log.entityName && (
                                    <div className="text-xs text-gray-500">{log.entityName}</div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-center gap-1 text-gray-700">
                                <MapPin className="w-3 h-3" />
                                <span>{formatLocation(log)}</span>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">{log.ipAddress}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {log.deviceType && (
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDeviceBadgeColor(log.deviceType)}`}>
                                    {log.deviceType}
                                  </span>
                                )}
                                {log.browser && (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                    {log.browser}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {log.responseTimeMs ? `${log.responseTimeMs}ms` : '-'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Audit Log View */}
            {activeTab === 'audit' && (
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resource</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredAuditLogs.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                            No admin actions found
                          </td>
                        </tr>
                      ) : (
                        filteredAuditLogs.map((log) => (
                          <tr key={log.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {new Date(log.createdAt).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="font-medium text-gray-900">{log.userEmail}</div>
                              <div className="text-xs text-gray-500">{log.userRole}</div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                {log.action}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {log.entityType && (
                                <div>
                                  <span className="font-medium">{log.entityType}</span>
                                  {log.entityId && <span className="text-gray-500"> #{log.entityId}</span>}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-center gap-1 text-gray-700">
                                <MapPin className="w-3 h-3" />
                                <span>{formatLocation(log)}</span>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">{log.ipAddress}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {log.deviceType && (
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDeviceBadgeColor(log.deviceType)}`}>
                                    {log.deviceType}
                                  </span>
                                )}
                                {log.browser && (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                    {log.browser}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {log.details && Object.keys(log.details).length > 0 && (
                                <details className="cursor-pointer">
                                  <summary className="text-blue-600 hover:text-blue-700">View</summary>
                                  <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-w-xs">
                                    {JSON.stringify(log.details, null, 2)}
                                  </pre>
                                </details>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuditLogViewer;
